import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { ExclamationTriangleIcon, HomeIcon, UserIcon } from '@heroicons/react/24/outline';

const UnauthorizedPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout, clearAll } = useAuthStore();

  // 当访问403页面时，说明认证状态有问题，应该清除无效状态
  useEffect(() => {
    console.log('403页面：检测到访问权限问题，可能需要清除无效的认证状态');
  }, []);

  const handleGoHome = () => {
    console.log('403页面：返回首页，清除所有认证状态');
    // 清除所有认证状态，确保首页显示未登录状态
    clearAll();
    navigate('/');
    // 强制刷新页面以确保状态完全重置
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  const handleGoToDashboard = () => {
    if (user?.role === 'teacher') {
      navigate('/teacher/dashboard');
    } else if (user?.role === 'admin') {
      navigate('/admin/dashboard');
    } else {
      navigate('/student/dashboard');
    }
  };

  const handleLogin = () => {
    console.log('403页面：重新登录，清除当前会话');
    clearAll(); // 使用更彻底的清除方法
    navigate('/login');
  };

  const handleClearAndGoHome = () => {
    console.log('403页面：强制清除认证状态并返回首页');
    clearAll();
    navigate('/');
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-red-100">
          <ExclamationTriangleIcon className="h-10 w-10 text-red-600" />
        </div>
        <div className="mt-6 text-center">
          <h1 className="text-4xl font-bold text-gray-900">403</h1>
          <h2 className="mt-2 text-xl font-semibold text-gray-700">访问被拒绝</h2>
          <p className="mt-2 text-sm text-gray-500">
            很抱歉，您没有权限访问此页面
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="space-y-4">
            {user ? (
              <>
                <div className="text-center">
                  <p className="text-sm text-gray-600">
                    当前登录用户: <span className="font-medium">{user.firstName} {user.lastName}</span>
                  </p>
                  <p className="text-xs text-gray-500">
                    用户角色: <span className="capitalize">{user.role}</span>
                  </p>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <p className="text-sm text-gray-600 text-center mb-4">
                    可能的原因：
                  </p>
                  <ul className="text-xs text-gray-500 space-y-1 mb-4">
                    <li>• 您的账户没有访问此功能的权限</li>
                    <li>• 此页面仅限特定用户角色访问</li>
                    <li>• 您的会话可能已过期</li>
                  </ul>
                </div>

                <div className="flex flex-col space-y-2">
                  <button
                    onClick={handleGoToDashboard}
                    className="w-full flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <HomeIcon className="h-4 w-4 mr-2" />
                    返回我的仪表盘
                  </button>
                  
                  <button
                    onClick={handleGoHome}
                    className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    返回首页
                  </button>

                  <button
                    onClick={handleLogin}
                    className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <UserIcon className="h-4 w-4 mr-2" />
                    重新登录
                  </button>

                  {/* 添加强制重置按钮 */}
                  <button
                    onClick={handleClearAndGoHome}
                    className="w-full flex justify-center items-center px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    🔄 清除状态并返回首页
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-4">
                    您需要登录才能访问此页面
                  </p>
                </div>

                <div className="flex flex-col space-y-2">
                  <button
                    onClick={handleLogin}
                    className="w-full flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <UserIcon className="h-4 w-4 mr-2" />
                    立即登录
                  </button>
                  
                  <button
                    onClick={handleGoHome}
                    className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    返回首页
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            如果您认为这是一个错误，请联系管理员获取帮助
          </p>
        </div>
      </div>
    </div>
  );
};

export default UnauthorizedPage; 